import React from 'react';
import { InputAdornment, Stack, Unstable_Grid2 as Grid } from '@mui/material';

import {
  ProductGroupSelectField,
  TextField,
  Typography,
  Select,
  KeyboardDateTimePicker,
  Tooltip,
  InfoOutlinedIcon,
} from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { Earning_Mechanism_Typename, OfferFilter } from '@occc/graphql';
import { LocationMultipleSelectField } from '@occc-app/routes/Location/components/LocationSelect/LocationMultipleSelectField';
import {
  MadePurchaseQualifiedEventsInitialFormValues,
  QualifiedEventsInitialFormValues,
  QualifiedEventType,
  TimePeriod,
} from '@occc-app/routes/Audience/types';
import { OfferSelect } from '@occc-app/routes/CampaignComponents/components/OfferSelect';
import { pluralize } from '@occc-app/lib/strings';
import { useChainId } from '@occc-app/Chain/hooks';

import { useField } from 'react-final-form';
import { LocationGroupMultipleSelect } from '@occc/feat-constraints';
import {
  countProductsByOptions,
  eventOptions,
  operatorOptions,
  timePeriodOptions,
} from './constants';

import { eventPurchaseConditionOperatorOptions, rollingWindowOptions } from '../constants';
import { PRODUCTS_SECTION_INFO_TEXT } from '../messages';

export type QualifiedEventsFormArrayItemProps = {
  fieldNamePrefix: string;
};

export const QualifiedEventsFormArrayItem = ({
  fieldNamePrefix,
}: QualifiedEventsFormArrayItemProps) => {
  const {
    input: { value: values },
  } = useField<QualifiedEventsInitialFormValues>(fieldNamePrefix, {
    subscription: { value: true },
  });
  const { event, times } = values;

  const chainId = useChainId();

  const makeOffersFilter = React.useCallback(
    (search: string): OfferFilter => {
      return {
        chainIdEquals: chainId,
        search: search || undefined,
        earningMechanismTypenameIncludes: [Earning_Mechanism_Typename.EarningMechanismLoyaltyEvent],
      };
    },
    [chainId],
  );

  return (
    <Grid container columnSpacing={3} rowSpacing={2}>
      <Grid xs={12}>
        <Typography variant='subtitle1'>Member has</Typography>
      </Grid>

      <Grid xs={12} md={6}>
        <Select name={`${fieldNamePrefix}.event`} label='Event' data={eventOptions} />
      </Grid>

      <Grid xs={12} md={3}>
        <Select
          name={`${fieldNamePrefix}.timesOperator`}
          label='Operator'
          data={eventPurchaseConditionOperatorOptions}
          formControlProps={{ size: 'medium', margin: 'none' }}
        />
      </Grid>
      <Grid xs={12} md={3}>
        <TextField
          name={`${fieldNamePrefix}.times`}
          label='Times'
          fieldProps={{
            parse: parseNumber,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>{pluralize(times ?? 0, 'time')}</InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid xs={12}>
        <Stack direction='row' alignItems='center'>
          <Typography variant='subtitle1'>Period of The purchase</Typography>
          {values.timePeriodType === TimePeriod.PreviousMonth && (
            <Tooltip
              title='The previous month calculation will include anybody who has performed a qualified event starting today.'
              placement='top'
            >
              <InfoOutlinedIcon fontSize='small' color='action' sx={{ ml: 0.5 }} />
            </Tooltip>
          )}
        </Stack>
      </Grid>

      <Grid xs={12} sm={4} md={3}>
        <Select name={`${fieldNamePrefix}.timePeriodType`} data={timePeriodOptions} />
      </Grid>

      {values.timePeriodType === TimePeriod.RollingWindow && (
        <Grid xs={12} sm={4} md={3}>
          <Select
            name={`${fieldNamePrefix}.timeWindow`}
            label='Rolling Window'
            data={rollingWindowOptions}
            formControlProps={{ size: 'medium', margin: 'none' }}
          />
        </Grid>
      )}

      {values.timePeriodType === TimePeriod.FixedWindow && (
        <>
          <Grid xs={12} sm={4} md={3}>
            <KeyboardDateTimePicker
              label='Start Date'
              name={`${fieldNamePrefix}.fixedWindow.startDate`}
              maxDate={values?.fixedWindow?.endDate || undefined}
              disablePast
            />
          </Grid>
          <Grid xs={12} sm={4} md={3}>
            <KeyboardDateTimePicker
              label='End Date'
              name={`${fieldNamePrefix}.fixedWindow.endDate`}
              minDate={values?.fixedWindow?.startDate || undefined}
              disablePast
            />
          </Grid>
        </>
      )}

      {event === QualifiedEventType.MadePurchase && (
        <>
          <Grid xs={12}>
            <Stack direction='row' alignItems='center'>
              <Typography variant='subtitle1'>Products Purchased</Typography>
              <Tooltip title={PRODUCTS_SECTION_INFO_TEXT} placement='top'>
                <InfoOutlinedIcon fontSize='small' color='action' sx={{ ml: 0.5 }} />
              </Tooltip>
            </Stack>
          </Grid>

          <Grid xs={12} md={8}>
            <ProductGroupSelectField
              inputProps={{
                margin: 'none',
              }}
              name={`${fieldNamePrefix}.productGroups`}
              label='Eligible Product Groups (Product in Any Group)'
            />
          </Grid>
          <Grid xs={12} md={4}>
            <Select
              name={`${fieldNamePrefix}.countProductsBy`}
              data={countProductsByOptions}
              label='Count Product by'
            />
          </Grid>

          <Grid
            xs={false}
            md={3}
            display={{
              md: 'block',
              display: 'none',
            }}
          />

          <Grid xs={12}>
            <Typography variant='subtitle1'>Member has Purchased</Typography>
          </Grid>

          <Grid xs={12} md={4}>
            <Select
              name={`${fieldNamePrefix}.${
                'operator' satisfies keyof MadePurchaseQualifiedEventsInitialFormValues
              }`}
              data={operatorOptions}
            />
          </Grid>

          <Grid xs={12} md={3}>
            <TextField
              name={`${fieldNamePrefix}.threshold`}
              label='Threshold'
              fieldProps={{
                parse: parseNumber,
              }}
            />
          </Grid>

          <Grid xs={12} md={6}>
            <Typography paragraph variant='subtitle1'>
              At This Location (optional)
            </Typography>

            <LocationMultipleSelectField
              inputProps={{
                margin: 'none',
              }}
              name={`${fieldNamePrefix}.locations`}
              label='Locations'
            />
          </Grid>

          <Grid xs={12} md={6}>
            <Typography paragraph variant='subtitle1'>
              At This Location Group (optional)
            </Typography>

            <LocationGroupMultipleSelect
              inputProps={{
                margin: 'none',
              }}
              name={`${fieldNamePrefix}.locationGroups`}
              label='Location Groups'
            />
          </Grid>
        </>
      )}

      {event === QualifiedEventType.FulfilledLoyaltyEvent && (
        <>
          <Grid xs={12}>
            <Typography variant='subtitle1'>Loyalty Event</Typography>
          </Grid>
          <Grid xs={12} md={6}>
            <OfferSelect
              makeOffersFilter={makeOffersFilter}
              name={`${fieldNamePrefix}.loyaltyEvent`}
              label='Loyalty Event'
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
